import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useGroupDetail } from '@/hooks/useGroups';
import { useCreateExpense, useGroupExpenses } from '@/hooks/useExpenses';
import { useCategories } from '@/hooks/useCategories';
import { useAuth } from '@/contexts/AuthContext';
import { SPLIT_TYPE_LABELS, FALLBACK_CURRENCIES_DATA } from '@sharecost/shared';
import type { SplitType } from '@sharecost/shared';
import { Loader2, ArrowLeft } from 'lucide-react';
import { RequiredMark, FieldError } from '@/components/common/FormHelpers';
import { SplitMembersSection } from '@/components/expenses/SplitMembersSection';
import { usePremium } from '@/hooks/usePremium';
import { checkGroupExpenseLimit } from '@/lib/premium';
import { PremiumPaywall } from '@/components/common/PremiumPaywall';
import { composeExpenseTimestamp } from '@/lib/dateTime';
import { useTranslation } from 'react-i18next';

export function AddExpensePage() {
  const { t } = useTranslation();
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: group } = useGroupDetail(groupId);
  const { data: categories } = useCategories();
  const createExpense = useCreateExpense();
  const { data: expenses } = useGroupExpenses(groupId);
  const { tier } = usePremium();

  // Check expense limit for free groups
  const expenseLimit = useMemo(() => {
    if (!group || !expenses) return { allowed: true, currentCount: 0, limit: 50 };
    return checkGroupExpenseLimit(tier, expenses.length, group.members);
  }, [tier, expenses, group]);

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [paidBy, setPaidBy] = useState(user?.id ?? '');
  const [categoryId, setCategoryId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [splitType, setSplitType] = useState<SplitType>('equal');
  const [splitWith, setSplitWith] = useState<string[]>([]);
  // Exact amounts / percentages / shares for non-equal splits
  const [exactAmounts, setExactAmounts] = useState<Record<string, string>>({});
  const [percentages, setPercentages] = useState<Record<string, string>>({});
  const [shares, setShares] = useState<Record<string, string>>({});
  const [tried, setTried] = useState(false);

  const parsedAmount = parseFloat(amount);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};

    if (!description.trim()) e.description = 'Description is required';
    if (!amount) e.amount = 'Amount is required';
    else if (isNaN(parsedAmount) || parsedAmount <= 0) e.amount = 'Enter a valid positive amount';

    if (!categoryId) e.category = 'Category is required';
    if (splitWith.length === 0) e.splitWith = 'Select at least one member';

    if (splitType === 'exact' && splitWith.length > 0 && !isNaN(parsedAmount) && parsedAmount > 0) {
      const sum = splitWith.reduce((s, id) => s + (parseFloat(exactAmounts[id]) || 0), 0);
      const diff = Math.abs(sum - parsedAmount);
      if (diff > 0.01) e.split = `Exact amounts sum to ${sum.toFixed(2)} but total is ${parsedAmount.toFixed(2)}`;
    }

    if (splitType === 'percentage' && splitWith.length > 0) {
      const sum = splitWith.reduce((s, id) => s + (parseFloat(percentages[id]) || 0), 0);
      if (Math.abs(sum - 100) > 0.1) e.split = `Percentages sum to ${sum.toFixed(1)}% — must be 100%`;
    }

    return e;
  }, [description, amount, parsedAmount, categoryId, splitWith, splitType, exactAmounts, percentages]);

  const hasErrors = Object.keys(errors).length > 0;

  const sortedCurrencies = useMemo(() => {
    const groupCode = group?.currency_code;
    return [...FALLBACK_CURRENCIES_DATA].sort((a, b) => {
      if (a.code === groupCode) return -1;
      if (b.code === groupCode) return 1;
      return a.code.localeCompare(b.code);
    });
  }, [group?.currency_code]);

  // Set defaults when group loads
  if (group && !currency) {
    setCurrency(group.currency_code);
    if (!splitWith.length) {
      setSplitWith(group.members.map(m => m.user_id));
    }
    if (!paidBy) {
      setPaidBy(user?.id ?? group.members[0]?.user_id ?? '');
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (hasErrors || !groupId || !user) return;

    try {
      await createExpense.mutateAsync({
        groupId,
        paidBy,
        createdBy: user.id,
        description,
        amount: parsedAmount,
        currencyCode: currency || group?.currency_code || 'CZK',
        categoryId: categoryId || undefined,
        date: date ? composeExpenseTimestamp(date) : undefined,
        splitType,
        splitWith,
        exactAmounts: splitType === 'exact'
          ? Object.fromEntries(splitWith.map(id => [id, parseFloat(exactAmounts[id]) || 0]))
          : undefined,
        percentages: splitType === 'percentage'
          ? Object.fromEntries(splitWith.map(id => [id, parseFloat(percentages[id]) || 0]))
          : undefined,
        shares: splitType === 'shares'
          ? Object.fromEntries(splitWith.map(id => [id, parseFloat(shares[id]) || 1]))
          : undefined,
      });

      navigate(`/groups/${groupId}`);
    } catch {
      // Error shown via createExpense.error
    }
  };

  if (!group) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('web:common.backTo', { name: group.name })}
      </button>

      <h1 className="text-2xl font-bold text-text-primary mb-6">{t('expenses:screen.addExpense')}</h1>

      <div className="relative">
      {!expenseLimit.allowed && (
        <PremiumPaywall
          feature={t('web:addExpensePage.expenseLimitReached')}
          description={t('web:addExpensePage.freeGroupsLimit', { limit: expenseLimit.limit })}
        />
      )}

      {createExpense.error && (
        <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {createExpense.error instanceof Error ? createExpense.error.message : t('web:addExpensePage.failedToCreateExpense')}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('expenses:form.description.label')}<RequiredMark /></label>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('web:addExpensePage.whatWasItFor')}
            className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 ${
              tried && errors.description
                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                : 'border-border-secondary focus:border-brand-500'
            }`}
          />
          {tried && <FieldError message={errors.description} />}
        </div>

        {/* Amount + Currency */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-text-secondary">{t('settlements:section.amount')}<RequiredMark /></label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 ${
                tried && errors.amount
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-border-secondary focus:border-brand-500'
              }`}
            />
            {tried && <FieldError message={errors.amount} />}
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary">{t('expenses:form.picker.currency')}</label>
            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border-secondary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              {sortedCurrencies.map(c => (
                <option key={c.code} value={c.code}>
                  {c.code} ({c.symbol})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Paid by */}
        <div>
          <label className="block text-sm font-medium text-text-secondary">{t('expenses:detail.paidBy')}</label>
          <select
            value={paidBy}
            onChange={e => setPaidBy(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border-secondary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            {group.members.map(m => (
              <option key={m.user_id} value={m.user_id}>
                {m.user?.display_name}{m.user_id === user?.id ? ` ${t('web:common.youSuffix')}` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Category + Date */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-text-secondary">{t('expenses:form.category.label')}<RequiredMark /></label>
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className={`mt-1 w-full rounded-lg border pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 ${
                tried && errors.category
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-border-secondary focus:border-brand-500'
              }`}
            >
              <option value="">{t('expenses:form.category.select')}</option>
              {categories?.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {tried && <FieldError message={errors.category} />}
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary">{t('settlements:view.date')}</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        {/* Split type */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">{t('expenses:detail.splitType')}</label>
          <div className="flex gap-2 flex-wrap">
            {(Object.entries(SPLIT_TYPE_LABELS) as [SplitType, string][]).map(([type, label]) => (
              <button
                key={type}
                type="button"
                onClick={() => setSplitType(type)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  splitType === type
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-tertiary text-text-secondary hover:bg-surface-inset'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Split with (member selection) */}
        <SplitMembersSection
          members={group.members}
          currentUserId={user?.id}
          splitType={splitType}
          splitWith={splitWith}
          onSplitWithChange={setSplitWith}
          totalAmount={parsedAmount}
          exactAmounts={exactAmounts}
          onExactAmountsChange={setExactAmounts}
          percentages={percentages}
          onPercentagesChange={setPercentages}
          shares={shares}
          onSharesChange={setShares}
          showErrors={tried}
          splitWithError={errors.splitWith}
          splitError={errors.split}
        />

        {/* Submit */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-border-secondary px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover"
          >
            {t('common:button.cancel')}
          </button>
          <button
            type="submit"
            disabled={createExpense.isPending || (tried && hasErrors)}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {createExpense.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('expenses:screen.addExpense')}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
